import { useState } from 'react';
import { motion } from 'framer-motion';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Download, Copy, Check, FileCode2, Terminal, AlertTriangle,
  Info, Package, Settings, Wrench, Globe, FileText, Eye,
  ChevronRight, ExternalLink,
} from 'lucide-react';
import { toast } from 'sonner';

// ── Reusable sub-components ────────────────────────────────────────────────

function Section({ id, icon: Icon, title, children }) {
  return (
    <motion.section
      id={id}
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.4 }}
      className="mb-16"
    >
      <div className="flex items-center gap-3 mb-8 pb-4 border-b border-border">
        <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
          <Icon className="w-5 h-5 text-primary" />
        </div>
        <h2 className="text-xl font-bold">{title}</h2>
      </div>
      {children}
    </motion.section>
  );
}

function CopyBtn({ text }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      onClick={() => { navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 1500); }}
      className="p-1.5 rounded hover:bg-white/10 transition-colors text-white/50 hover:text-white shrink-0"
    >
      {copied ? <Check className="w-3.5 h-3.5 text-green-400" /> : <Copy className="w-3.5 h-3.5" />}
    </button>
  );
}

function CodeBlock({ code, lang = 'bash', className = '' }) {
  return (
    <div className={`relative rounded-xl overflow-hidden border border-border ${className}`}>
      <div className="flex items-center justify-between bg-muted/60 px-4 py-2 border-b border-border">
        <div className="flex items-center gap-2">
          <div className="flex gap-1.5">
            <span className="w-3 h-3 rounded-full bg-red-500/60" />
            <span className="w-3 h-3 rounded-full bg-yellow-500/60" />
            <span className="w-3 h-3 rounded-full bg-green-500/60" />
          </div>
          <span className="text-xs font-mono text-muted-foreground ml-1">{lang}</span>
        </div>
        <CopyBtn text={code} />
      </div>
      <pre className="bg-[#0d0d0d] p-4 overflow-x-auto text-sm leading-relaxed">
        <code className="font-mono text-[#e2e8f0] whitespace-pre">{code}</code>
      </pre>
    </div>
  );
}

function StepCard({ number, title, children }) {
  return (
    <div className="flex gap-5">
      <div className="flex flex-col items-center shrink-0">
        <div className="w-9 h-9 rounded-xl bg-primary/15 border border-primary/25 flex items-center justify-center text-primary font-black text-sm shrink-0">
          {number}
        </div>
        <div className="w-px flex-1 bg-border mt-2 mb-0" />
      </div>
      <div className="pb-10 flex-1 min-w-0">
        <h3 className="font-bold text-base mb-3">{title}</h3>
        <div className="space-y-3">{children}</div>
      </div>
    </div>
  );
}

function Note({ variant = 'info', children }) {
  const styles = {
    info:    { bg: 'bg-blue-500/10 border-blue-500/25',   icon: Info,          iconColor: 'text-blue-400' },
    warning: { bg: 'bg-yellow-500/10 border-yellow-500/25', icon: AlertTriangle, iconColor: 'text-yellow-500' },
    tip:     { bg: 'bg-primary/10 border-primary/25',     icon: Info,          iconColor: 'text-primary' },
  };
  const s = styles[variant];
  return (
    <div className={`flex items-start gap-3 rounded-xl border p-4 text-sm ${s.bg}`}>
      <s.icon className={`w-4 h-4 mt-0.5 shrink-0 ${s.iconColor}`} />
      <div className="leading-relaxed text-foreground/80">{children}</div>
    </div>
  );
}

function FileRow({ name, desc, href }) {
  return (
    <tr className="border-b border-border last:border-0 hover:bg-muted/30 transition-colors">
      <td className="p-4 pl-5">
        <div className="flex items-center gap-2">
          <FileCode2 className="w-4 h-4 text-primary shrink-0" />
          <code className="text-sm font-mono text-primary font-bold">{name}</code>
        </div>
      </td>
      <td className="p-4 text-sm text-muted-foreground">{desc}</td>
      <td className="p-4 text-right">
        <a
          href={href}
          download
          className="flex items-center gap-1 text-xs font-bold text-primary hover:text-primary/80 transition-colors ml-auto w-fit"
        >
          <Download className="w-3.5 h-3.5" />
          Download
        </a>
      </td>
    </tr>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────

const NAV = [
  { id: 'files',          label: 'Dateien'        },
  { id: 'requirements',   label: 'Voraussetzungen' },
  { id: 'installation',   label: 'Installation'   },
  { id: 'configuration',  label: 'Konfiguration'  },
  { id: 'variables',      label: 'Variablen'      },
  { id: 'troubleshooting', label: 'Fehler'        },
];

export default function WhmcsGuidePage() {
  const downloadAll = () => {
    ['header.tpl', 'clientareaproductdetails.tpl', 'clientarea.tpl'].forEach((file, i) => {
      setTimeout(() => {
        const a = document.createElement('a');
        a.href = `/whmcs-template/${file}`;
        a.download = file;
        a.click();
      }, i * 300);
    });
    toast.success('Alle Template-Dateien werden heruntergeladen…');
  };

  return (
    <Layout>
      <div className="section-spacing">
        <div className="container-main">

          {/* ── Hero ── */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-16">
            <div className="flex flex-col md:flex-row items-start md:items-end justify-between gap-6">
              <div>
                <Badge className="mb-3 bg-primary/10 text-primary border-primary/20">WHMCS Integration</Badge>
                <h1 className="text-4xl md:text-5xl font-black mb-3">
                  WHMCS <span className="text-gradient">Installationsanleitung</span>
                </h1>
                <p className="text-muted-foreground max-w-xl">
                  Schritt-für-Schritt-Anleitung zur Integration des DeinServerHost Designs in deinen WHMCS-Kundenbereich –
                  inkl. Header, Proxmox-Modul-Template und vollständiger CSS-Variablen.
                </p>
              </div>
              <div className="flex flex-wrap gap-3 shrink-0">
                <Button onClick={downloadAll} className="bg-primary hover:bg-primary/90 glow-hover font-bold">
                  <Download className="w-4 h-4 mr-2" />
                  Alle Templates laden
                </Button>
                <a href="/whmcs-preview" target="_blank" rel="noopener noreferrer">
                  <Button variant="outline" className="border-primary/30 hover:border-primary">
                    <Eye className="w-4 h-4 mr-2" />
                    VPS-Vorschau
                  </Button>
                </a>
                <a href="/whmcs-dashboard" target="_blank" rel="noopener noreferrer">
                  <Button variant="outline" className="border-primary/30 hover:border-primary">
                    <Globe className="w-4 h-4 mr-2" />
                    Dashboard-Vorschau
                  </Button>
                </a>
              </div>
            </div>

            {/* Quick Nav */}
            <div className="flex flex-wrap gap-2 mt-8">
              {NAV.map(({ id, label }) => (
                <a
                  key={id}
                  href={`#${id}`}
                  className="text-xs font-medium px-3 py-1.5 rounded-full glass border border-border hover:border-primary/40 hover:text-primary transition-colors"
                >
                  {label}
                </a>
              ))}
            </div>
          </motion.div>

          {/* ── Files ── */}
          <Section id="files" icon={Package} title="Enthaltene Dateien">
            <div className="glass rounded-xl border border-border overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left p-4 pl-5 text-xs font-bold uppercase tracking-wider text-muted-foreground">Datei</th>
                    <th className="text-left p-4 text-xs font-bold uppercase tracking-wider text-muted-foreground">Beschreibung</th>
                    <th className="p-4 text-xs font-bold uppercase tracking-wider text-muted-foreground" />
                  </tr>
                </thead>
                <tbody>
                  <FileRow
                    name="header.tpl"
                    desc="Globaler WHMCS-Header für alle Seiten – Logo, Navigation, Dark Mode Toggle, Benutzer-Badge, Mobile-Menü, Ankündigungs-Bar."
                    href="/whmcs-template/header.tpl"
                  />
                  <FileRow
                    name="clientareaproductdetails.tpl"
                    desc="Überschreibt die Produkt-Detailseite im WHMCS Kundenbereich – vollständig gestaltet mit DSH Design System."
                    href="/whmcs-template/clientareaproductdetails.tpl"
                  />
                  <FileRow
                    name="clientarea.tpl"
                    desc="Proxmox-Modul-Template: Power-Steuerung, Ressourcen-Gauges (CPU/RAM/Disk/Traffic), Verbindungsinfos, Backups, Snapshots, SSH Quick-Connect."
                    href="/whmcs-template/clientarea.tpl"
                  />
                  <FileRow
                    name="dsh-design-tokens.css"
                    desc="Portables CSS Design-System: alle CSS-Variablen, Tokens, Utility-Klassen – für WHMCS und andere Anwendungen."
                    href="/dsh-design-tokens.css"
                  />
                </tbody>
              </table>
            </div>
          </Section>

          {/* ── Requirements ── */}
          <Section id="requirements" icon={FileText} title="Voraussetzungen">
            <div className="grid sm:grid-cols-3 gap-4">
              {[
                { label: 'WHMCS',        value: '8.x oder neuer',              icon: Globe },
                { label: 'PHP',          value: '8.1+ / Smarty 4.x',          icon: Terminal },
                { label: 'Proxmox-Modul', value: 'ModulesGarden Proxmox VE', icon: Settings },
              ].map(r => (
                <div key={r.label} className="glass rounded-xl border border-border p-5 flex items-start gap-4">
                  <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                    <r.icon className="w-4 h-4 text-primary" />
                  </div>
                  <div>
                    <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-1">{r.label}</div>
                    <div className="font-semibold text-sm">{r.value}</div>
                  </div>
                </div>
              ))}
            </div>
          </Section>

          {/* ── Installation ── */}
          <Section id="installation" icon={Terminal} title="Installation – Schritt für Schritt">
            <div className="space-y-0">

              <StepCard number="1" title="Design-System CSS hochladen">
                <p className="text-sm text-muted-foreground">Lade <code className="text-primary font-mono text-xs">dsh-design-tokens.css</code> auf deinen WHMCS-Server hoch:</p>
                <CodeBlock lang="path" code="/assets/css/dsh-design-tokens.css" />
                <Note variant="info">Erstelle den Ordner <code>/assets/css/</code> falls er noch nicht existiert.</Note>
              </StepCard>

              <StepCard number="2" title="WHMCS-Theme anlegen">
                <p className="text-sm text-muted-foreground">Erstelle ein neues Theme auf Basis des Standard-Themes:</p>
                <CodeBlock lang="bash" code={`# Im WHMCS-Root-Verzeichnis:\ncp -r templates/twenty-one templates/dsh`} />
                <Note variant="tip">
                  <strong>Alternativ:</strong> Du kannst die <code>.tpl</code>-Dateien auch direkt in dein bestehendes aktives Theme kopieren – ohne ein neues Theme anzulegen.
                </Note>
              </StepCard>

              <StepCard number="3" title="header.tpl einspielen">
                <p className="text-sm text-muted-foreground">Kopiere <code className="text-primary font-mono text-xs">header.tpl</code> in den Theme-Ordner:</p>
                <CodeBlock lang="path" code={`/templates/dsh/header.tpl`} />
                <p className="text-sm text-muted-foreground">Lege außerdem folgende Assets an:</p>
                <CodeBlock lang="path" code={`/templates/dsh/css/dsh-design-tokens.css   ← Design System CSS\n/templates/dsh/img/logo-dark.svg           ← Logo (weiß, für Dark Mode)\n/templates/dsh/img/logo-light.svg          ← Logo (dunkel, für Light Mode)`} />
                <Note variant="warning">
                  Die <code>header.tpl</code> öffnet <code>{'<div class="dsh-page-wrapper">'}</code> und <code>{'<div class="dsh-content-container">'}</code>.
                  Deine <code>footer.tpl</code> muss diese beiden <code>{'</div>'}</code>-Tags sowie <code>{'</body></html>'}</code> schließen.
                </Note>
              </StepCard>

              <StepCard number="4" title="clientareaproductdetails.tpl einspielen">
                <p className="text-sm text-muted-foreground">Kopiere die Datei in den Theme-Ordner:</p>
                <CodeBlock lang="path" code={`/templates/dsh/clientareaproductdetails.tpl`} />
              </StepCard>

              <StepCard number="5" title="clientarea.tpl ins ModulesGarden-Modul einspielen">
                <p className="text-sm text-muted-foreground">Kopiere das Template in den Templates-Ordner des ModulesGarden Proxmox VE Moduls:</p>
                <CodeBlock lang="path" code={`/modules/servers/ProxmoxVE/templates/clientarea.tpl`} />
                <Note variant="info">
                  Der genaue Ordnerpfad kann je nach installierter Version variieren. Prüfe den Ordnernamen unter <code>/modules/servers/</code> auf deinem Server.
                </Note>
              </StepCard>

              <StepCard number="6" title="Theme in WHMCS aktivieren">
                <p className="text-sm text-muted-foreground">Aktiviere das neue Theme in den WHMCS-Einstellungen:</p>
                <CodeBlock lang="whmcs" code={`Admin → Setup → General Settings → Display → Template\n→ Wähle "dsh" (oder deinen Theme-Namen)`} />
              </StepCard>

            </div>
          </Section>

          {/* ── Configuration ── */}
          <Section id="configuration" icon={Settings} title="Konfiguration">
            <div className="space-y-8">

              <div>
                <h3 className="text-sm font-bold uppercase tracking-wider text-muted-foreground mb-4">Dark Mode Toggle</h3>
                <p className="text-sm text-muted-foreground mb-3">
                  Die <code className="text-primary font-mono text-xs">header.tpl</code> enthält bereits automatische Theme-Erkennung via <code>prefers-color-scheme</code> und einen Toggle-Button.
                  Für einzelne Templates füge diesen Snippet in den <code>{'<head>'}</code> ein:
                </p>
                <CodeBlock lang="html" code={`<script>\n  (function() {\n    var saved = localStorage.getItem('dsh-theme') || 'dark';\n    document.documentElement.classList.toggle('dsh-dark', saved === 'dark');\n    document.documentElement.setAttribute('data-theme', saved);\n  })();\n</script>`} />
              </div>

              <div>
                <h3 className="text-sm font-bold uppercase tracking-wider text-muted-foreground mb-4">Google Fonts abschalten (DSGVO)</h3>
                <p className="text-sm text-muted-foreground mb-3">
                  Um Google Fonts nicht über externe Server zu laden, entferne die <code>preconnect</code>- und <code>fonts.googleapis.com</code>-Zeilen aus dem Template und hoste die Schriften selbst:
                </p>
                <div className="flex items-center gap-3">
                  <a
                    href="https://gwfh.mranftl.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-sm font-bold text-primary hover:text-primary/80 transition-colors"
                  >
                    <ExternalLink className="w-4 h-4" />
                    Google Fonts Helper (gwfh.mranftl.com)
                  </a>
                </div>
                <p className="text-xs text-muted-foreground mt-2">Benötigte Fonts: <code className="font-mono">Outfit</code>, <code className="font-mono">DM Sans</code>, <code className="font-mono">JetBrains Mono</code></p>
              </div>

              <div>
                <h3 className="text-sm font-bold uppercase tracking-wider text-muted-foreground mb-4">Light Mode erzwingen</h3>
                <p className="text-sm text-muted-foreground mb-3">
                  Entferne <code className="text-primary font-mono text-xs">dsh-dark</code> aus dem Root-Element und setze das <code>data-theme</code>-Attribut:
                </p>
                <CodeBlock lang="html" code={`<!-- Light Mode -->\n<div class="dsh-root" data-theme="light">\n\n<!-- Dark Mode (Standard) -->\n<div class="dsh-root dsh-dark" data-theme="dark">`} />
              </div>

            </div>
          </Section>

          {/* ── Variable Mapping ── */}
          <Section id="variables" icon={FileCode2} title="Variable Mapping – ModulesGarden Proxmox VE">
            <p className="text-sm text-muted-foreground mb-6">
              Die folgenden Smarty-Variablen stellt das <strong>ModulesGarden Proxmox VE</strong> Modul im <code className="text-primary font-mono text-xs">clientarea.tpl</code> zur Verfügung:
            </p>
            <div className="glass rounded-xl border border-border overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border">
                    {['Beschreibung', 'Variable', 'Hinweis'].map(h => (
                      <th key={h} className="text-left p-4 first:pl-5 text-xs font-bold uppercase tracking-wider text-muted-foreground">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {[
                    ['VM-Status',        '$vmstatus',    'z.B. running, stopped'],
                    ['CPU-Auslastung',   '$cpu',         'Wert 0–100 (Prozent)'],
                    ['RAM (bytes)',      '$mem',         'Genutzte RAM in Bytes'],
                    ['Disk (bytes)',     '$disk',        'Genutzter Speicher in Bytes'],
                    ['IPv4-Adresse',     '$mainip',      'Primäre IP des Servers'],
                    ['Backups',          '$backups',     'Array mit Backup-Objekten'],
                    ['Snapshots',        '$snapshots',   'Array mit Snapshot-Objekten'],
                    ['Hostname',         '$domain',      'WHMCS-Produktdomainname'],
                    ['Server-Passwort',  '$password',    'Gesetztes WHMCS-Passwort'],
                  ].map(([desc, varName, hint]) => (
                    <tr key={varName} className="border-b border-border last:border-0 hover:bg-muted/20 transition-colors">
                      <td className="p-4 pl-5 text-sm font-medium">{desc}</td>
                      <td className="p-4">
                        <code className="text-sm font-mono text-primary font-bold">{varName}</code>
                      </td>
                      <td className="p-4 text-xs text-muted-foreground">{hint}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Note variant="tip" className="mt-4">
              Alle verfügbaren Variablen anzeigen: <code className="font-mono text-xs">{'{$smarty.var|@print_r}'}</code> temporär ins Template einfügen.
            </Note>
          </Section>

          {/* ── Troubleshooting ── */}
          <Section id="troubleshooting" icon={Wrench} title="Fehlerbehebung">
            <div className="space-y-6">
              {[
                {
                  title: 'CSS wird nicht geladen',
                  body: 'Überprüfe, ob die CSS-Datei unter dem erwarteten Pfad erreichbar ist:',
                  code: 'https://deinedomain.de/assets/css/dsh-design-tokens.css',
                  lang: 'url',
                },
                {
                  title: 'Logo fehlt',
                  body: 'Stelle sicher, dass die Logo-Dateien angelegt sind oder passe den Pfad im Template an:',
                  code: '/templates/dsh/img/logo-dark.svg\n/templates/dsh/img/logo-light.svg',
                  lang: 'path',
                },
                {
                  title: 'Smarty-Fehler bei Variablen',
                  body: 'Aktiviere Smarty-Debugging temporär, um alle verfügbaren Variablen anzuzeigen:',
                  code: '{debug}',
                  lang: 'smarty',
                },
                {
                  title: 'Template wird nicht angezeigt',
                  body: 'Prüfe, ob WHMCS den richtigen Theme-Ordner verwendet:',
                  code: 'Admin → Configuration → System Settings → General Settings → Display',
                  lang: 'whmcs',
                },
              ].map(item => (
                <div key={item.title} className="glass rounded-xl border border-border p-5">
                  <div className="flex items-center gap-2 mb-3">
                    <AlertTriangle className="w-4 h-4 text-yellow-500 shrink-0" />
                    <h3 className="font-bold text-sm">{item.title}</h3>
                  </div>
                  <p className="text-sm text-muted-foreground mb-3">{item.body}</p>
                  <CodeBlock code={item.code} lang={item.lang} />
                </div>
              ))}
            </div>
          </Section>

          {/* ── License ── */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="glass rounded-2xl border border-border p-8 text-center"
          >
            <h3 className="font-black text-lg mb-2">Lizenz & Nutzung</h3>
            <p className="text-sm text-muted-foreground max-w-xl mx-auto leading-relaxed">
              Diese Templates sind ausschließlich für den Betrieb unter der Domain <strong>deinserverhost.de</strong> und
              zugehörigen WHMCS-Instanzen bestimmt. Eine Weitergabe oder Nutzung für andere Marken ist nicht gestattet.
            </p>
            <div className="flex flex-wrap justify-center gap-3 mt-6">
              <Button onClick={downloadAll} className="bg-primary hover:bg-primary/90 glow-hover font-bold">
                <Download className="w-4 h-4 mr-2" />
                Alle Templates herunterladen
              </Button>
              <a href="/design-system">
                <Button variant="outline" className="border-primary/30 hover:border-primary">
                  Design System ansehen
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </a>
            </div>
          </motion.div>

        </div>
      </div>
    </Layout>
  );
}
